import { useState, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Upload, AlertCircle, Filter, Camera, Save, SkipForward, Trash2, Loader2, ListChecks, ChevronLeft, ChevronRight, RefreshCw, ExternalLink, Edit2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { backupRecordsToExcel } from "@/utils/countyResearchBackup";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";

interface WarrenCountyTextParserProps {
  onRecordsSaved?: () => void;
}

interface ImportResult {
  success: number;
  failed: number;
  duplicates: number;
  familyTransfers: number;
  failedRecords: Array<{ line: number; error: string }>;
}

interface ExtractedRecord {
  document_number: string | null;
  document_type: string | null;
  filing_date: string | null;
  grantor_name: string | null;
  grantor_address: string | null;
  grantee_name: string | null;
  grantee_address: string | null;
  property_address: string | null;
  legal_description: string | null;
  loan_amount: number | null;
  interest_rate: number | null;
  monthly_payment: number | null;
  term_months: number | null;
  balloon_amount: number | null;
  balloon_due_date: string | null;
}

interface ReviewRecord {
  id: string;
  document_number: string;
  filing_date: string;
  grantor_name: string;
  grantee_name: string;
  grantor_address: string | null;
  document_type: string;
  legal_description: string | null;
}

export const WarrenCountyTextParser = ({ onRecordsSaved }: WarrenCountyTextParserProps) => {
  // Import mode state
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [pastedText, setPastedText] = useState("");
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // Screenshot capture state
  const [imageData, setImageData] = useState<string | null>(null);
  const [extractedData, setExtractedData] = useState<ExtractedRecord | null>(null);
  const [savedCount, setSavedCount] = useState(0);
  const [skippedCount, setSkippedCount] = useState(0);

  // Review mode state
  const [reviewRecords, setReviewRecords] = useState<ReviewRecord[]>([]);
  const [reviewIndex, setReviewIndex] = useState(0);
  const [isLoadingReview, setIsLoadingReview] = useState(false);
  const [reviewImageData, setReviewImageData] = useState<string | null>(null);
  const [reviewExtractedData, setReviewExtractedData] = useState<ExtractedRecord | null>(null);
  const [reviewSavedCount, setReviewSavedCount] = useState(0);
  const [reviewSkippedCount, setReviewSkippedCount] = useState(0);

  // Clipboard routing (helps when paste event doesn't hit the intended div)
  const [pasteTarget, setPasteTarget] = useState<'review' | 'screenshot'>('review');

  const extractImageDataUrlFromClipboard = useCallback(async (clipboardData?: DataTransfer | null) => {
    const items = clipboardData?.items;
    if (!items) return null;

    for (const item of items) {
      if (item.type?.startsWith('image/')) {
        const file = item.getAsFile();
        if (!file) continue;

        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        return base64;
      }
    }

    return null;
  }, []);

  const pasteScreenshotFromClipboard = useCallback(async (target: 'review' | 'screenshot') => {
    // Fallback for browsers/environments where onPaste doesn't deliver image blobs reliably.
    try {
      setPasteTarget(target);

      // navigator.clipboard.read() requires HTTPS + user gesture and may be unsupported in some browsers.
      const items = await (navigator.clipboard as any)?.read?.();
      if (!items || items.length === 0) {
        toast.error('Clipboard is empty or blocked by the browser');
        return;
      }

      for (const item of items) {
        const type = item.types?.find((t: string) => t.startsWith('image/'));
        if (!type) continue;

        const blob: Blob = await item.getType(type);
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        if (target === 'review') {
          setReviewImageData(base64);
          setReviewExtractedData(null);
          toast.success('Screenshot captured! Click "Extract" to process.');
        } else {
          setImageData(base64);
          setExtractedData(null);
          toast.success('Screenshot captured! Click "Extract Data" to process.');
        }
        return;
      }

      toast.error('No image found in clipboard');
    } catch (err: any) {
      toast.error(err?.message || 'Clipboard access blocked by the browser');
    }
  }, []);

  useEffect(() => {
    const onWindowPaste = async (e: ClipboardEvent) => {
      const base64 = await extractImageDataUrlFromClipboard(e.clipboardData);
      if (!base64) return;

      if (pasteTarget === 'review') {
        setReviewImageData(base64);
        setReviewExtractedData(null);
        toast.success('Screenshot captured! Click "Extract" to process.');
      } else {
        setImageData(base64);
        setExtractedData(null);
        toast.success('Screenshot captured! Click "Extract Data" to process.');
      }
    };

    window.addEventListener('paste', onWindowPaste);
    return () => window.removeEventListener('paste', onWindowPaste);
  }, [extractImageDataUrlFromClipboard, pasteTarget]);

  const parseDate = (dateStr: string): string | null => {
    if (!dateStr || dateStr.trim() === '') return null;
    
    // Handle "MM/DD/YYYY HH:MM:SS AM/PM" format from Fidlar
    const dateMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (dateMatch) {
      const [, month, day, year] = dateMatch;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    // Already in YYYY-MM-DD format
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateStr;
    }
    
    return null;
  };

  const getLastName = (name: string): string => {
    if (!name) return '';
    const cleaned = name.trim().toUpperCase();
    // For names like "SMITH JOHN" or "SMITH, JOHN", get first word
    const parts = cleaned.split(/[\s,]+/);
    return parts[0] || '';
  };

  const isFamilyTransfer = (grantorName: string, granteeName: string): boolean => {
    const grantorLast = getLastName(grantorName);
    const granteeLast = getLastName(granteeName);
    
    if (!grantorLast || !granteeLast) return false;
    return grantorLast === granteeLast;
  };

  const parseWarrenCountyText = (text: string): { records: any[], familyCount: number } => {
    console.log('=== STARTING WARREN COUNTY PARSE ===');
    console.log('Raw text sample:', text.substring(0, 500));
    
    const lines = text.split('\n').filter(line => line.trim());
    console.log('Total lines:', lines.length);
    
    const records: any[] = [];
    let familyCount = 0;

    const isDocNumLine = (s: string) => /^\d{4}-\d{6}$/.test(s.trim());
    const isTypeLine = (s: string) => {
      const v = s.trim().toUpperCase();
      return v.length > 0 && v.length <= 12;
    };
    const isParcelLine = (s: string) => /^\d{7,}$/.test(s.trim()) || s.trim().toLowerCase() === 'multiple parcels';

    let i = 0;
    while (i < lines.length) {
      const line = lines[i].trim();

      if (isDocNumLine(line)) {
        const documentNumber = line;
        const documentType = (lines[i + 1] || '').trim();
        const dateStr = (lines[i + 2] || '').trim();
        const grantorName = (lines[i + 3] || '').trim();
        const granteeName = (lines[i + 4] || '').trim();
        const maybeParcel = (lines[i + 5] || '').trim();

        if (!documentType || !dateStr || !grantorName || !granteeName || !isTypeLine(documentType)) {
          i += 1;
          continue;
        }

        const filingDate = parseDate(dateStr);
        if (!filingDate) {
          i += 1;
          continue;
        }

        if (isFamilyTransfer(grantorName, granteeName)) {
          familyCount++;
          i += 6;
          continue;
        }

        const parcelNumber = isParcelLine(maybeParcel) ? maybeParcel : '';

        records.push({
          county: 'Warren',
          document_number: documentNumber,
          filing_date: filingDate,
          document_type: documentType,
          grantor_name: grantorName,
          grantee_name: granteeName,
          legal_description: parcelNumber ? `Parcel: ${parcelNumber}` : null,
          search_date: new Date().toISOString().split('T')[0],
          is_potential_owner_finance: ['FS', 'MTG', 'L/C', 'LAND CONTRACT', 'LC'].includes(documentType.toUpperCase()),
          confidence_score: 70,
          detection_reasons: [`Document type: ${documentType}`, 'Warren County Fidlar format'],
        });

        i += 6;
        continue;
      }

      // Fallback: one-line row parsing (tab/space separated)
      console.log('Processing line:', line);
      let parts = line.split('\t').map(p => p.trim()).filter(p => p);

      if (parts.length < 5) {
        const docMatch = line.match(/^(\d{4}-\d{6})\s+(\w+(?:\/\w+)?|TOD AFF|AFFIDAVIT)\s+(\d{1,2}\/\d{1,2}\/\d{4}\s+\d{1,2}:\d{2}:\d{2}\s*[AP]M?)\s*\.{0,3}\s*(.+?)\s{2,}(.+?)\s+(\d{7,}|Multiple Parcels)?$/i);
        if (docMatch) {
          parts = [docMatch[1], docMatch[2], docMatch[3], docMatch[4], docMatch[5], docMatch[6] || ''];
        } else {
          parts = line.split(/\s{2,}/).map(p => p.trim()).filter(p => p);
        }
      }

      if (parts.length >= 5) {
        const documentNumber = parts[0];
        const documentType = parts[1];
        const dateStr = parts[2];
        const grantorName = parts[3].replace(/\.{2,}$/, '').trim();
        const granteeName = parts[4];
        const parcelNumber = parts[5] || '';

        if (documentNumber.match(/^\d{4}-\d{6}$/)) {
          const filingDate = parseDate(dateStr);
          if (filingDate && !isFamilyTransfer(grantorName, granteeName)) {
            records.push({
              county: 'Warren',
              document_number: documentNumber,
              filing_date: filingDate,
              document_type: documentType,
              grantor_name: grantorName,
              grantee_name: granteeName,
              legal_description: parcelNumber ? `Parcel: ${parcelNumber}` : null,
              search_date: new Date().toISOString().split('T')[0],
              is_potential_owner_finance: ['FS', 'MTG', 'L/C', 'LAND CONTRACT', 'LC'].includes(documentType.toUpperCase()),
              confidence_score: 70,
              detection_reasons: [`Document type: ${documentType}`, 'Warren County Fidlar format'],
            });
          }
        }
      }

      i += 1;
    }

    console.log(`=== PARSE COMPLETE: Found ${records.length} records, ${familyCount} family transfers filtered ===`);
    return { records, familyCount };
  };

  const handleImport = async () => {
    if (!pastedText.trim()) {
      toast.error("Please paste some text first");
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setImportResult(null);

    try {
      const { records, familyCount } = parseWarrenCountyText(pastedText);
      
      if (records.length === 0) {
        toast.error("No valid records found in the pasted text");
        setIsProcessing(false);
        return;
      }

      console.log('Parsed records:', records);

      let successCount = 0;
      let failedCount = 0;
      let duplicateCount = 0;
      const failedRecords: Array<{ line: number; error: string }> = [];

      for (let i = 0; i < records.length; i++) {
        try {
          const { data: existing } = await supabase
            .from('county_research')
            .select('id')
            .eq('county', 'Warren')
            .eq('document_number', records[i].document_number)
            .maybeSingle();

          if (existing) {
            duplicateCount++;
            console.log('Duplicate found:', records[i].document_number);
          } else {
            const { error } = await supabase
              .from('county_research')
              .insert(records[i]);

            if (error) throw error;
            successCount++;
          }
        } catch (error: any) {
          console.error(`Failed to insert record ${i}:`, error);
          failedCount++;
          failedRecords.push({
            line: i + 1,
            error: error.message || 'Unknown error'
          });
        }

        setProgress(Math.round(((i + 1) / records.length) * 100));
      }

      setImportResult({
        success: successCount,
        failed: failedCount,
        duplicates: duplicateCount,
        familyTransfers: familyCount,
        failedRecords
      });

      if (successCount > 0) {
        backupRecordsToExcel(records.filter((_, i) => !failedRecords.some(f => f.line === i + 1)), 'Warren', 'warren_text');
        toast.success(`Successfully imported ${successCount} record(s)`);
        onRecordsSaved?.();
      }

      setPastedText('');

      if (failedCount > 0) {
        toast.error(`Failed to import ${failedCount} record(s)`);
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to process records');
    } finally {
      setIsProcessing(false);
    }
  };

  // Screenshot capture handlers
  const handlePaste = useCallback(async (e: React.ClipboardEvent) => {
    const base64 = await extractImageDataUrlFromClipboard(e.clipboardData);
    if (!base64) {
      toast.error('No image found in clipboard');
      return;
    }

    setImageData(base64);
    setExtractedData(null);
    toast.success('Screenshot captured! Click "Extract Data" to process.');
  }, [extractImageDataUrlFromClipboard]);

  const handleExtract = async () => {
    if (!imageData) {
      toast.error('No screenshot to process');
      return;
    }

    setIsProcessing(true);
    try {
      const { data, error } = await supabase.functions.invoke('extract-contract-screenshot', {
        body: { imageData, county: 'warren' }
      });

      if (error) throw error;

      if (data?.extractedData) {
        setExtractedData(data.extractedData);
        toast.success('Data extracted! Review and save.');
      } else {
        toast.error('No data could be extracted');
      }
    } catch (error) {
      console.error('Extraction error:', error);
      toast.error(`Extraction failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSave = async () => {
    if (!extractedData?.document_number) {
      toast.error('Document number is required');
      return;
    }

    setIsProcessing(true);
    try {
      const { data: existing } = await supabase
        .from('county_research')
        .select('id')
        .eq('county', 'Warren')
        .eq('document_number', extractedData.document_number)
        .maybeSingle();

      if (existing) {
        // Update existing record with extracted address
        const { error } = await supabase
          .from('county_research')
          .update({
            grantor_address: extractedData.grantor_address,
            grantee_address: extractedData.grantee_address,
            property_address: extractedData.property_address,
            loan_amount: extractedData.loan_amount,
            interest_rate: extractedData.interest_rate,
            monthly_payment: extractedData.monthly_payment,
            term_months: extractedData.term_months,
            balloon_amount: extractedData.balloon_amount,
            balloon_due_date: extractedData.balloon_due_date,
            address_extraction_method: 'screenshot_ai',
            address_needs_review: false
          })
          .eq('id', existing.id);

        if (error) throw error;
        toast.success(`Updated: ${extractedData.document_number}`);
      } else {
        // Insert new record
        const record = {
          county: 'Warren',
          document_number: extractedData.document_number,
          document_type: extractedData.document_type || 'LAND CONTRACT',
          filing_date: extractedData.filing_date || new Date().toISOString().split('T')[0],
          grantor_name: extractedData.grantor_name,
          grantor_address: extractedData.grantor_address,
          grantee_name: extractedData.grantee_name,
          grantee_address: extractedData.grantee_address,
          property_address: extractedData.property_address,
          legal_description: extractedData.legal_description,
          loan_amount: extractedData.loan_amount,
          interest_rate: extractedData.interest_rate,
          monthly_payment: extractedData.monthly_payment,
          term_months: extractedData.term_months,
          balloon_amount: extractedData.balloon_amount,
          balloon_due_date: extractedData.balloon_due_date,
          is_potential_owner_finance: true,
          confidence_score: 90,
          address_extraction_method: 'screenshot_ai',
          status: 'new',
          address_needs_review: !extractedData.grantor_address
        };

        const { error } = await supabase.from('county_research').insert(record);
        if (error) throw error;
        backupRecordsToExcel([record], 'Warren', 'warren_screenshot');
        toast.success(`Saved: ${extractedData.document_number}`);
      }

      setSavedCount(prev => prev + 1);
      handleClear();
      onRecordsSaved?.();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(`Save failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSkip = () => {
    setSkippedCount(prev => prev + 1);
    handleClear();
    toast.info('Record skipped');
  };

  const handleClear = () => {
    setImageData(null);
    setExtractedData(null);
  };

  // Review mode functions
  const loadReviewRecords = async () => {
    setIsLoadingReview(true);
    try {
      const { data, error } = await supabase
        .from('county_research')
        .select('id, document_number, filing_date, grantor_name, grantee_name, grantor_address, document_type, legal_description')
        .eq('county', 'Warren')
        .is('deleted_at', null)
        .or('grantor_address.is.null,address_needs_review.eq.true')
        .order('filing_date', { ascending: false })
        .limit(100);

      if (error) throw error;

      setReviewRecords(data || []);
      setReviewIndex(0);
      setReviewImageData(null);
      setReviewExtractedData(null);

      if (data && data.length > 0) {
        toast.success(`Loaded ${data.length} records to review`);
      } else {
        toast.info('No Warren County records need review');
      }
    } catch (error) {
      console.error('Error loading review records:', error);
      toast.error('Failed to load records');
    } finally {
      setIsLoadingReview(false);
    }
  };

  const currentReviewRecord = reviewRecords[reviewIndex];

  const handleReviewPaste = useCallback(async (e: React.ClipboardEvent) => {
    const base64 = await extractImageDataUrlFromClipboard(e.clipboardData);
    if (!base64) {
      toast.error('No image found in clipboard');
      return;
    }

    setReviewImageData(base64);
    setReviewExtractedData(null);
    toast.success('Screenshot captured! Click "Extract" to process.');
  }, [extractImageDataUrlFromClipboard]);

  const handleReviewExtract = async () => {
    if (!reviewImageData) {
      toast.error('No screenshot to process');
      return;
    }

    setIsProcessing(true);
    try {
      const { data, error } = await supabase.functions.invoke('extract-contract-screenshot', {
        body: { imageData: reviewImageData, county: 'warren', recordId: currentReviewRecord?.id }
      });

      if (error) throw error;

      if (data?.extractedData) {
        setReviewExtractedData(data.extractedData);
        toast.success('Data extracted!');
      } else {
        toast.error('No data could be extracted');
      }
    } catch (error) {
      console.error('Extraction error:', error);
      toast.error(`Extraction failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReviewSave = async () => {
    if (!currentReviewRecord) return;

    setIsProcessing(true);
    try {
      const updateData: Record<string, unknown> = {
        address_needs_review: false,
        address_extraction_method: 'screenshot_ai',
        updated_at: new Date().toISOString()
      };

      if (reviewExtractedData?.grantor_address) {
        updateData.grantor_address = reviewExtractedData.grantor_address;
      }
      if (reviewExtractedData?.grantee_address) {
        updateData.grantee_address = reviewExtractedData.grantee_address;
      }
      if (reviewExtractedData?.property_address) {
        updateData.property_address = reviewExtractedData.property_address;
      }
      if (reviewExtractedData?.loan_amount) {
        updateData.loan_amount = reviewExtractedData.loan_amount;
      }
      if (reviewExtractedData?.interest_rate) {
        updateData.interest_rate = reviewExtractedData.interest_rate;
      }

      const { error } = await supabase
        .from('county_research')
        .update(updateData)
        .eq('id', currentReviewRecord.id);

      if (error) throw error;

      toast.success(`Saved: ${currentReviewRecord.document_number}`);
      setReviewSavedCount(prev => prev + 1);

      // Move to next record
      if (reviewIndex < reviewRecords.length - 1) {
        setReviewIndex(prev => prev + 1);
        setReviewImageData(null);
        setReviewExtractedData(null);
      } else {
        toast.success('Finished reviewing all records!');
        loadReviewRecords();
      }

      onRecordsSaved?.();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(`Save failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReviewSkip = () => {
    setReviewSkippedCount(prev => prev + 1);
    if (reviewIndex < reviewRecords.length - 1) {
      setReviewIndex(prev => prev + 1);
      setReviewImageData(null);
      setReviewExtractedData(null);
    } else {
      toast.info('No more records to review');
    }
  };

  const handleReviewDelete = async () => {
    if (!currentReviewRecord) return;

    try {
      const { error } = await supabase
        .from('county_research')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', currentReviewRecord.id);

      if (error) throw error;

      toast.success('Record deleted');
      
      // Remove from list and adjust index
      const newRecords = reviewRecords.filter((_, idx) => idx !== reviewIndex);
      setReviewRecords(newRecords);
      
      if (reviewIndex >= newRecords.length && newRecords.length > 0) {
        setReviewIndex(newRecords.length - 1);
      }
      
      setReviewImageData(null);
      setReviewExtractedData(null);
      onRecordsSaved?.();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete record');
    }
  };

  const handleReviewPrev = () => {
    if (reviewIndex > 0) {
      setReviewIndex(prev => prev - 1);
      setReviewImageData(null);
      setReviewExtractedData(null);
    }
  };

  const handleReviewNext = () => {
    if (reviewIndex < reviewRecords.length - 1) {
      setReviewIndex(prev => prev + 1);
      setReviewImageData(null);
      setReviewExtractedData(null);
    }
  };

  // Keyboard shortcuts for review mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!currentReviewRecord) return;
      
      // Don't trigger if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      if (e.key === 'ArrowLeft') {
        handleReviewPrev();
      } else if (e.key === 'ArrowRight') {
        handleReviewNext();
      } else if (e.key === 's' || e.key === 'S') {
        if (reviewExtractedData) {
          handleReviewSave();
        }
      } else if (e.key === 'k' || e.key === 'K') {
        handleReviewSkip();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentReviewRecord, reviewExtractedData, reviewIndex]);

  const getFidlarDocumentUrl = (docNumber: string) => {
    // Warren County Fidlar URL pattern
    return `https://oh.warrenrecorder.us/recorder/eagleweb/viewDoc.jsp?node=DOC${docNumber.replace('-', '')}`;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Filter className="h-5 w-5" />
          Warren County Parser (Fidlar/AvaWeb)
        </CardTitle>
        <CardDescription>
          Import data from Fidlar search results or capture seller addresses from contract screenshots.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Tabs defaultValue="import" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="import">
              <Upload className="w-4 h-4 mr-2" />
              Import
            </TabsTrigger>
            <TabsTrigger value="review">
              <ListChecks className="w-4 h-4 mr-2" />
              Review Mode
            </TabsTrigger>
            <TabsTrigger value="screenshot">
              <Camera className="w-4 h-4 mr-2" />
              Screenshot
            </TabsTrigger>
          </TabsList>

          {/* IMPORT TAB */}
          <TabsContent value="import" className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Paste Warren County Data</label>
              <Textarea
                placeholder="Copy rows from Fidlar search results and paste here...
Example:
2025-033433	R/M	12/30/2025 1:43:38 PM	BANK OF AMERICA B...	WEMPA BRAD J	1204262018"
                value={pastedText}
                onChange={(e) => setPastedText(e.target.value)}
                className="min-h-[300px] font-mono text-sm"
              />
            </div>

            <Button 
              onClick={handleImport}
              disabled={isProcessing || !pastedText.trim()}
              className="w-full"
            >
              {isProcessing ? (
                <>
                  <Upload className="mr-2 h-4 w-4 animate-pulse" />
                  Processing... {progress}%
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Import Warren County Records
                </>
              )}
            </Button>

            {isProcessing && (
              <Progress value={progress} className="w-full" />
            )}

            {importResult && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-2">
                    <p className="font-semibold">Import Results:</p>
                    <p className="text-foreground">✓ Imported: {importResult.success} records</p>
                    {importResult.duplicates > 0 && (
                      <p className="text-muted-foreground">○ Duplicates skipped: {importResult.duplicates}</p>
                    )}
                    {importResult.familyTransfers > 0 && (
                      <p className="text-muted-foreground">○ Family transfers filtered: {importResult.familyTransfers}</p>
                    )}
                    {importResult.failed > 0 && (
                      <>
                        <p className="text-foreground">✗ Failed: {importResult.failed} records</p>
                        {importResult.failedRecords.length > 0 && (
                          <details className="text-sm">
                            <summary className="cursor-pointer">View failed records</summary>
                            <ul className="mt-2 space-y-1">
                              {importResult.failedRecords.map((fail, idx) => (
                                <li key={idx}>
                                  Line {fail.line}: {fail.error}
                                </li>
                              ))}
                            </ul>
                          </details>
                        )}
                      </>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </TabsContent>

          {/* REVIEW MODE TAB */}
          <TabsContent value="review" className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Cycle through imported Warren County records and paste screenshots to extract seller addresses.
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={loadReviewRecords}
                disabled={isLoadingReview}
              >
                {isLoadingReview ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Load Records
                  </>
                )}
              </Button>
            </div>

            {reviewRecords.length === 0 ? (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Click "Load Records" to fetch Warren County records that need seller address review.
                </AlertDescription>
              </Alert>
            ) : (
              <>
                {/* Navigation and stats */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleReviewPrev}
                      disabled={reviewIndex === 0}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm font-medium">
                      {reviewIndex + 1} of {reviewRecords.length}
                    </span>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleReviewNext}
                      disabled={reviewIndex >= reviewRecords.length - 1}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex gap-4 text-sm">
                    <span className="text-primary">✓ Saved: {reviewSavedCount}</span>
                    <span className="text-muted-foreground">○ Skipped: {reviewSkippedCount}</span>
                  </div>
                </div>

                {/* Current record info */}
                {currentReviewRecord && (
                  <Card className="bg-muted/30">
                    <CardContent className="pt-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <Badge variant="outline">{currentReviewRecord.document_type}</Badge>
                        <a 
                          href={getFidlarDocumentUrl(currentReviewRecord.document_number)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline flex items-center gap-1"
                        >
                          <ExternalLink className="h-3 w-3" />
                          Open in Fidlar
                        </a>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Document #:</span>
                          <p className="font-mono font-medium">{currentReviewRecord.document_number}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Filing Date:</span>
                          <p>{currentReviewRecord.filing_date}</p>
                        </div>
                        <div className="col-span-2">
                          <span className="text-muted-foreground">Seller (Grantor):</span>
                          <p className="font-medium">{currentReviewRecord.grantor_name}</p>
                        </div>
                        <div className="col-span-2">
                          <span className="text-muted-foreground">Buyer (Grantee):</span>
                          <p>{currentReviewRecord.grantee_name}</p>
                        </div>
                        {currentReviewRecord.grantor_address && (
                          <div className="col-span-2">
                            <span className="text-muted-foreground">Existing Address:</span>
                            <p className="text-primary">{currentReviewRecord.grantor_address}</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Screenshot paste area */}
                <div
                  onPaste={handleReviewPaste}
                  onClick={(e) => {
                    setPasteTarget('review');
                    (e.currentTarget as HTMLDivElement).focus();
                  }}
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary ${
                    reviewImageData ? 'border-primary bg-primary/10' : 'border-muted-foreground/25 hover:border-primary/50'
                  }`}
                  tabIndex={0}
                >
                  {reviewImageData ? (
                    <div className="space-y-2">
                      <img 
                        src={reviewImageData} 
                        alt="Captured screenshot" 
                        className="max-h-48 mx-auto rounded border"
                      />
                      <p className="text-sm text-primary font-medium">Screenshot captured!</p>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <Camera className="h-8 w-8 mx-auto text-muted-foreground" />
                      <p className="font-medium text-sm">Click here and paste screenshot (Ctrl+V)</p>
                      <p className="text-xs text-muted-foreground">
                        Keyboard: ← → navigate, S save, K skip
                      </p>
                      <div className="flex justify-center pt-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            pasteScreenshotFromClipboard('review');
                          }}
                        >
                          Paste Screenshot
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Extract button */}
                {reviewImageData && !reviewExtractedData && (
                  <Button 
                    onClick={handleReviewExtract} 
                    disabled={isProcessing}
                    className="w-full"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Extracting...
                      </>
                    ) : (
                      'Extract Seller Address'
                    )}
                  </Button>
                )}

                {/* Extracted data */}
                {reviewExtractedData && (
                  <Card className="bg-primary/5 border-primary/20">
                    <CardContent className="pt-4 space-y-2">
                      <h4 className="font-semibold text-sm flex items-center gap-2">
                        Extracted Data
                        <Edit2 className="h-3 w-3 text-muted-foreground" />
                      </h4>
                      <div className="text-sm space-y-2">
                        <div>
                          <label className="text-muted-foreground text-xs">Seller's Mailing Address (editable):</label>
                          <Input
                            value={reviewExtractedData.grantor_address || ''}
                            onChange={(e) => setReviewExtractedData({
                              ...reviewExtractedData,
                              grantor_address: e.target.value
                            })}
                            placeholder="Enter or correct seller address"
                            className="mt-1 font-medium"
                          />
                        </div>
                        {reviewExtractedData.loan_amount && (
                          <div className="flex gap-4">
                            <span>Loan: ${reviewExtractedData.loan_amount.toLocaleString()}</span>
                            {reviewExtractedData.interest_rate && <span>Rate: {reviewExtractedData.interest_rate}%</span>}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Action buttons */}
                <div className="flex gap-2">
                  <Button 
                    onClick={handleReviewSave} 
                    disabled={isProcessing || !reviewExtractedData}
                    className="flex-1"
                  >
                    <Save className="mr-2 h-4 w-4" />
                    Save & Next (S)
                  </Button>
                  <Button variant="outline" onClick={handleReviewSkip}>
                    <SkipForward className="mr-2 h-4 w-4" />
                    Skip (K)
                  </Button>
                  <Button variant="destructive" size="icon" onClick={handleReviewDelete}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </>
            )}
          </TabsContent>

          {/* SCREENSHOT CAPTURE TAB */}
          <TabsContent value="screenshot" className="space-y-4">
            <Alert>
              <Camera className="h-4 w-4" />
              <AlertDescription>
                <strong>Seller Address Capture:</strong> Paste a screenshot of the land contract document to extract the seller's (grantor's) mailing address using AI.
              </AlertDescription>
            </Alert>

            {/* Session stats */}
            {(savedCount > 0 || skippedCount > 0) && (
              <div className="flex gap-4 text-sm">
                <span className="text-primary">✓ Saved: {savedCount}</span>
                <span className="text-muted-foreground">○ Skipped: {skippedCount}</span>
              </div>
            )}

            {/* Paste area */}
            <div
              onPaste={handlePaste}
              onClick={() => setPasteTarget('screenshot')}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                imageData ? 'border-primary bg-primary/10' : 'border-muted-foreground/25 hover:border-primary/50'
              }`}
              tabIndex={0}
            >
              {imageData ? (
                <div className="space-y-4">
                  <img 
                    src={imageData} 
                    alt="Captured screenshot" 
                    className="max-h-64 mx-auto rounded border"
                  />
                  <p className="text-sm text-primary font-medium">Screenshot captured!</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Camera className="h-12 w-12 mx-auto text-muted-foreground" />
                  <p className="font-medium">Click here and paste screenshot (Ctrl+V)</p>
                  <p className="text-sm text-muted-foreground">
                    Capture the contract page showing the seller's name and mailing address
                  </p>
                  <div className="flex justify-center pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        pasteScreenshotFromClipboard('screenshot');
                      }}
                    >
                      Paste Screenshot
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Action buttons */}
            {imageData && (
              <div className="flex gap-2">
                <Button 
                  onClick={handleExtract} 
                  disabled={isProcessing}
                  className="flex-1"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Extracting...
                    </>
                  ) : (
                    'Extract Data'
                  )}
                </Button>
                <Button variant="outline" onClick={handleClear}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}

            {/* Extracted data display */}
            {extractedData && (
              <Card className="bg-muted/50">
                <CardContent className="pt-4 space-y-3">
                  <h4 className="font-semibold">Extracted Data</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">Document #:</span>
                      <p className="font-mono">{extractedData.document_number || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Filing Date:</span>
                      <p>{extractedData.filing_date || 'N/A'}</p>
                    </div>
                    <div className="col-span-2">
                      <span className="text-muted-foreground">Seller (Grantor):</span>
                      <p className="font-medium">{extractedData.grantor_name || 'N/A'}</p>
                    </div>
                    <div className="col-span-2">
                      <span className="text-muted-foreground">Seller's Mailing Address:</span>
                      <p className="font-medium text-primary">{extractedData.grantor_address || 'Not found'}</p>
                    </div>
                    <div className="col-span-2">
                      <span className="text-muted-foreground">Buyer (Grantee):</span>
                      <p>{extractedData.grantee_name || 'N/A'}</p>
                    </div>
                    {extractedData.loan_amount && (
                      <div>
                        <span className="text-muted-foreground">Loan Amount:</span>
                        <p>${extractedData.loan_amount.toLocaleString()}</p>
                      </div>
                    )}
                    {extractedData.interest_rate && (
                      <div>
                        <span className="text-muted-foreground">Interest Rate:</span>
                        <p>{extractedData.interest_rate}%</p>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button onClick={handleSave} disabled={isProcessing} className="flex-1">
                      <Save className="mr-2 h-4 w-4" />
                      Save Record
                    </Button>
                    <Button variant="outline" onClick={handleSkip}>
                      <SkipForward className="mr-2 h-4 w-4" />
                      Skip
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
